/**
 * @file Komendy.tsx
 * @description "Komendy" — catalog of Marcin's main slash-commands (/deep, /watek,
 * /workflows…). Grouped cards (GET /api/commands) by function (Planowanie / Decyzje /
 * Wiedza / Audyt / Komunikacja / Wbudowane); clicking a card opens its profile
 * (/komendy/:id) with goal/when/process + a connection graph. Copy is Polish.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Command, RefreshCw, ChevronRight, AlertTriangle, Share2 } from "lucide-react";
import { api } from "../lib/api";
import type { CommandsPayload } from "../lib/api";
import { cmdIcon } from "../components/commands/icons";

export function Komendy() {
  const [data, setData] = useState<CommandsPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.commands.list());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const total = useMemo(() => (data?.groups ?? []).reduce((a, g) => a + g.items.length, 0), [data]);

  return (
    <div className="animate-fade-in space-y-8 max-w-4xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-accent/15 flex items-center justify-center flex-shrink-0">
            <Command className="w-4.5 h-4.5 text-accent" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-gray-100">Komendy</h1>
            <p className="text-xs text-gray-500">
              Twoje główne zadania po slash · {total} komend
              {data && data.undocumented.length > 0 && (
                <span className="ml-2 text-amber-400">
                  · {data.undocumented.length} nieudokumentowanych
                </span>
              )}
            </p>
          </div>
        </div>
        <button onClick={load} className="btn-ghost flex-shrink-0" disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Odśwież
        </button>
      </div>

      {error && !data && (
        <div className="card p-4 flex items-center gap-3 border-red-500/30">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <p className="text-xs text-gray-500 font-mono truncate">{error}</p>
        </div>
      )}

      {loading && !data && (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="animate-pulse h-14 bg-surface-2 rounded-xl" />
          ))}
        </div>
      )}

      {data?.groups.map((g) =>
        g.items.length === 0 ? null : (
          <section key={g.key}>
            <h2
              className="text-sm font-semibold text-gray-100 mb-3 pl-2"
              style={{ borderLeft: `3px solid ${g.color}` }}
            >
              {g.label} <span className="text-gray-500 font-normal">· {g.items.length}</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {g.items.map((it) => {
                const Icon = cmdIcon(it.icon);
                return (
                  <Link
                    key={it.id}
                    to={`/komendy/${it.id}`}
                    className="card card-hover p-3 flex items-start gap-3 no-underline"
                  >
                    <span
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ backgroundColor: `${g.color}1f`, color: g.color }}
                    >
                      <Icon className="w-4 h-4" aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="font-mono text-[13px] text-accent">{it.slash}</span>
                        <span className="text-sm font-medium text-gray-100 truncate">
                          {it.name}
                        </span>
                        {it.present === false && (
                          <span
                            className="text-[9px] text-amber-400 border border-amber-500/30 rounded px-1 py-0.5 flex-shrink-0"
                            title="W rejestrze, ale kolektor nie znalazł jej na dysku"
                          >
                            drift
                          </span>
                        )}
                      </span>
                      <span className="block text-[11px] text-gray-400 line-clamp-2 mt-0.5">
                        {it.goal}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[11px] text-gray-400 mt-1">
                        <Share2 className="w-3 h-3" aria-hidden="true" /> {it.connections} powiązań
                      </span>
                    </span>
                    <ChevronRight
                      className="w-4 h-4 text-gray-500 flex-shrink-0 mt-1"
                      aria-hidden="true"
                    />
                  </Link>
                );
              })}
            </div>
          </section>
        )
      )}
    </div>
  );
}
